import {
  validations as userValidations,
  KEYS as userKeys,
} from './user';

import {
  validations as collectionValidation,
  KEYS as collectionKeys,
} from './collection';

import {
  validations as productValidations,
  KEYS as productKeys,
} from './product';

import {
  validations as vendorValidations,
  KEYS as vendorKeys,
} from './vendor';

import {
  validations as productBulkEditValidations,
  KEYS as productBulkEditKeys,
} from './product.bulk';

import {
  validations as orderValidations,
  variantSelectionValidations,
  KEYS as orderKeys,
} from './order';

import {
  validations as additionalChargesValidations,
  KEYS as additionalChargesKeys,
} from './order.charges';

import {
  validations as shippingInfoValidations,
  KEYS as shippingInfoKeys,
} from './order.shipping';

import {
  validations as invoiceValidations,
  KEYS as invoiceKeys,
} from './order.invoice';

import {
  validations as paymentInfoValidations,
  KEYS as paymentInfoKeys,
} from './order.payment';

import {
  validations as transactionValidations,
  KEYS as transactionKeys,
} from './order.transaction';

import {
  validations as appConfigValidations,
  KEYS as appConfigKeys,
} from './app.config';

import { type Validation } from './utils';

interface ValidateResult {
  isValid: boolean;
  errors: Record<string, unknown>;
  errorMessages: Record<string, unknown>;
}

function validate(
  validations: Validation[],
  values: Record<string, unknown>,
  touched: Record<string, unknown>,
  // Optional: most server callers validate non-subscription forms and omit it
  // (so it arrives `undefined` at runtime, as it always has); forwarded as-is
  // to each validator, which keeps the non-optional `Subscriptions` contract.
  subscriptions?: Subscriptions,
): ValidateResult {
  let isValid = true;
  const errors: Record<string, unknown> = {};
  const errorMessages: Record<string, unknown> = {};

  validations.forEach((validation) => {
    const result = validation(values, subscriptions as Subscriptions);
    if (typeof result !== 'object') {
      // validate pass
      return;
    }

    const keys = Object.keys(result);
    if (keys.length !== 1) {
      // unexpected key length
      return;
    }

    const controlName = keys[0]!;
    if (!touched[controlName]) {
      // control not touched
      return;
    }

    isValid = false;

    const controlResult = result[controlName];
    if (Array.isArray(controlResult)) {
      // array means the validation target is nested
      // expand the result
      errors[controlName] = controlResult.map((vret) => {
        return Object.keys(vret)
          // .filter((k) => k !== 'rowIndex')
          .reduce<Record<string, unknown>>((acc, k) => {
            if (k === 'rowIndex') {
              acc[k] = vret[k];
            } else {
              acc[k] = true;
            }
            return acc;
          }, {});
      });
    } else {
      errors[controlName] = true;
    }

    Object.assign(errorMessages, result);
  });

  return {
    isValid,
    errors,
    errorMessages,
  };
}

function buildAllTouched(keys: string[]): Record<string, boolean> {
  return keys.reduce<Record<string, boolean>>((ret, key) => {
    ret[key] = true;
    return ret;
  }, {});
}

export {
  validate,
  buildAllTouched,

  appConfigValidations,
  appConfigKeys,
  userValidations,
  userKeys,
  collectionValidation,
  collectionKeys,
  productValidations,
  productKeys,
  vendorValidations,
  vendorKeys,
  productBulkEditValidations,
  productBulkEditKeys,
  orderValidations,
  variantSelectionValidations,
  orderKeys,
  additionalChargesValidations,
  additionalChargesKeys,
  shippingInfoValidations,
  shippingInfoKeys,
  invoiceValidations,
  invoiceKeys,
  paymentInfoValidations,
  paymentInfoKeys,
  transactionValidations,
  transactionKeys,
};
